import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AuthGate } from '../features/auth/auth-gate.js';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/card.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import { Button } from '../components/ui/button.js';
import { Table, THead, TBody, TR, TH, TD } from '../components/ui/table.js';
import { StatusPill, type PostStatus } from '../components/ui/status-pill.js';
import { STATUS_ICON } from '../components/ui/icons.js';
import { cn } from '../lib/utils.js';
import { HOTELS_QUERY_KEY, listHotels, type Hotel } from '../features/hotels/api.js';
import { HotelFilterDropdown } from '../features/hotels/hotel-filter-dropdown.js';
import { getFavoriteHotels } from '../features/dashboard/api.js';
import {
  listContent,
  type ContentQuery,
  type ContentRow,
  type ContentSort,
} from '../features/content/api.js';
import { STATUS_GROUPS, type StatusGroup } from '../features/content/status-groups.js';
import { ReservePanel } from '../features/reserve/reserve-panel.js';

/* ------------------------------------------------------------------ */
/* tabs                                                               */
/* ------------------------------------------------------------------ */

type ContentTab = 'content' | 'reserve';

const TABS: Array<{ value: ContentTab; label: string }> = [
  { value: 'content', label: 'Tartalmak' },
  { value: 'reserve', label: 'Tartalék anyagok' },
];

/* ------------------------------------------------------------------ */
/* constants                                                          */
/* ------------------------------------------------------------------ */

const SORT_OPTIONS: Array<{ value: ContentSort; label: string }> = [
  { value: 'newest', label: 'Legújabb' },
  { value: 'oldest', label: 'Legrégebbi' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'status', label: 'Státusz' },
];

/* ------------------------------------------------------------------ */
/* helpers                                                            */
/* ------------------------------------------------------------------ */

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Toggle a whole display group in the raw status array. A group counts as
 * active when every member status is selected; toggling it off removes all
 * members, toggling it on adds the missing ones.
 */
function toggleGroup(arr: PostStatus[], group: StatusGroup): PostStatus[] {
  const active = group.statuses.every((s) => arr.includes(s));
  if (active) return arr.filter((s) => !group.statuses.includes(s));
  const next = new Set(arr);
  for (const s of group.statuses) next.add(s);
  return [...next];
}

/* ------------------------------------------------------------------ */
/* channel glyphs                                                     */
/* ------------------------------------------------------------------ */

function ChannelTag({ label }: { label: string }) {
  return (
    <span className="inline-flex h-5 items-center rounded-sm bg-[var(--app-surface-2)] px-1.5 font-mono text-meta font-semibold text-[var(--fg-2)]">
      {label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* page                                                               */
/* ------------------------------------------------------------------ */

/**
 * Org-wide content/post browser body (filters card + table). Rendered inside
 * the "Tartalmak" tab; the page-level header owns the <h1>. Reports its current
 * row count upward so the header badge can reflect the active tab.
 */
function ContentPanel({ onRowCount }: { onRowCount: (count: number) => void }) {
  const navigate = useNavigate();

  const [statuses, setStatuses] = useState<PostStatus[]>([]);
  const [hotelIds, setHotelIds] = useState<string[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<ContentSort>('newest');

  const hotelsQuery = useQuery<Hotel[]>({
    queryKey: HOTELS_QUERY_KEY,
    queryFn: listHotels,
  });
  const favoritesQuery = useQuery<string[]>({
    queryKey: ['dashboard-favorites'],
    queryFn: getFavoriteHotels,
  });

  const query: ContentQuery = useMemo(
    () => ({
      ...(statuses.length ? { status: statuses } : {}),
      ...(hotelIds.length ? { hotelId: hotelIds } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(q.trim() ? { q: q.trim() } : {}),
      sort,
    }),
    [statuses, hotelIds, from, to, q, sort],
  );

  const listQuery = useQuery<ContentRow[]>({
    queryKey: ['content', query],
    queryFn: () => listContent(query),
  });

  const rows = listQuery.data ?? [];
  const hotels = hotelsQuery.data ?? [];

  useEffect(() => {
    onRowCount(rows.length);
  }, [rows.length, onRowCount]);

  const hasFilters = statuses.length > 0 || hotelIds.length > 0 || !!from || !!to || !!q.trim();

  function clearFilters() {
    setStatuses([]);
    setHotelIds([]);
    setFrom('');
    setTo('');
    setQ('');
    setSort('newest');
  }

  function openRow(row: ContentRow) {
    // Open the plan view AND the content editor drawer for the clicked post:
    // the plan route reads `?post=<id>` and opens the EditorDrawer on mount.
    void navigate({
      to: '/app/plans/$hotelId/$year/$month',
      params: { hotelId: row.hotelId, year: String(row.year), month: String(row.month) },
      search: { post: row.id },
    });
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Szűrők</CardTitle>
          <CardDescription>
            Az összes szálloda tartalmai egy helyen — szűrj státusz, hotel, időszak vagy kulcsszó
            szerint.
          </CardDescription>
        </CardHeader>

        <div className="space-y-4">
          {/* Status pills */}
          <div className="space-y-1.5">
            <Label>Státusz</Label>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_GROUPS.map((group) => {
                const active = group.statuses.every((s) => statuses.includes(s));
                return (
                  <button
                    key={group.key}
                    type="button"
                    onClick={() => setStatuses((prev) => toggleGroup(prev, group))}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-pill border px-2 py-0.5 text-meta font-medium leading-tight transition-colors',
                      active
                        ? 'border-transparent text-white'
                        : 'border-[var(--app-divider-2)] bg-[var(--app-surface)] text-[var(--fg-muted)] hover:border-[var(--brand-400)] hover:text-[var(--ink-700)]',
                    )}
                    style={active ? { background: group.dot } : undefined}
                    aria-pressed={active}
                  >
                    {group.iconKey ? (
                      (() => {
                        const GroupIcon = STATUS_ICON[group.iconKey];
                        return <GroupIcon size={13} aria-hidden="true" />;
                      })()
                    ) : (
                      <span
                        aria-hidden="true"
                        className="inline-block h-1.5 w-1.5 rounded-full"
                        style={{ background: active ? 'var(--app-surface)' : group.dot }}
                      />
                    )}
                    {group.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Hotel — searchable dropdown, favorites first then alphabetical */}
          <div className="space-y-1.5">
            <Label>Hotel</Label>
            {hotels.length === 0 ? (
              <p className="text-meta text-[var(--fg-faint)]">
                {hotelsQuery.isLoading ? 'Hotelek betöltése…' : 'Nincs elérhető hotel.'}
              </p>
            ) : (
              <HotelFilterDropdown
                hotels={hotels}
                favoriteIds={favoritesQuery.data ?? []}
                selected={hotelIds}
                onChange={setHotelIds}
              />
            )}
          </div>

          {/* Date range + search + sort */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label htmlFor="content-from">Kezdő dátum</Label>
              <Input
                id="content-from"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="content-to">Záró dátum</Label>
              <Input
                id="content-to"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="content-q">Keresés</Label>
              <Input
                id="content-q"
                value={q}
                placeholder="Cím vagy szöveg…"
                onChange={(e) => setQ(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="content-sort">Rendezés</Label>
              <select
                id="content-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value as ContentSort)}
                className="mt-1.5 flex h-10 w-full rounded-md border border-[var(--app-divider-2)] bg-[var(--app-surface)] px-3 text-body"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {hasFilters && (
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={clearFilters}>
                Szűrők törlése
              </Button>
            </div>
          )}
        </div>
      </Card>

      <Card className="p-0">
        {listQuery.isLoading ? (
          <p className="p-6 text-center text-body text-[var(--fg-muted)]">Betöltés…</p>
        ) : listQuery.error ? (
          <p className="p-6 text-center text-body text-[var(--danger)]">
            {(listQuery.error as Error).message}
          </p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-center text-body text-[var(--fg-muted)]">
            Nincs a szűrőnek megfelelő tartalom.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <THead>
                <TR>
                  <TH className="whitespace-nowrap px-4 pl-6">Dátum</TH>
                  <TH className="whitespace-nowrap px-4">Hotel</TH>
                  <TH className="whitespace-nowrap px-4">Terv</TH>
                  <TH className="whitespace-nowrap px-4">Státusz</TH>
                  <TH className="w-full">Cím</TH>
                  <TH className="whitespace-nowrap px-4 pr-6 text-right">Csatorna</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((row) => (
                  <TR
                    key={row.id}
                    className="cursor-pointer hover:bg-bg/40 [&>td]:py-2.5"
                    onClick={() => openRow(row)}
                  >
                    <TD className="whitespace-nowrap px-4 pl-6 font-mono tabular-nums text-[var(--fg-2)]">
                      {formatDate(row.scheduledFor)}
                    </TD>
                    <TD className="whitespace-nowrap px-4 font-medium text-[var(--ink-700)]">
                      {row.hotelName}
                    </TD>
                    <TD className="whitespace-nowrap px-4 font-mono tabular-nums text-[var(--fg-2)]">
                      {row.year}. {String(row.month).padStart(2, '0')}
                    </TD>
                    <TD className="px-4">
                      <StatusPill status={row.status} scheduledFor={row.scheduledFor} size="sm" />
                    </TD>
                    <TD className="w-full max-w-0 truncate">
                      {row.title ? (
                        <span className="text-[var(--ink-700)]">{row.title}</span>
                      ) : (
                        <span className="italic text-[var(--fg-faint)]">— nincs téma —</span>
                      )}
                    </TD>
                    <TD className="whitespace-nowrap px-4 pr-6 text-right">
                      <span className="inline-flex items-center gap-1">
                        {row.publishToFb && <ChannelTag label="FB" />}
                        {row.publishToIg && <ChannelTag label="IG" />}
                        {!row.publishToFb && !row.publishToIg && (
                          <span className="text-[var(--fg-faint)]">—</span>
                        )}
                      </span>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* page (header + tab bar)                                             */
/* ------------------------------------------------------------------ */

function ContentInner() {
  const navigate = useNavigate();
  const { tab } = Route.useSearch();

  // Row count of the content browser, lifted up so the header badge can show
  // it on the "Tartalmak" tab. Only meaningful on that tab.
  const [contentCount, setContentCount] = useState(0);

  function selectTab(next: ContentTab) {
    void navigate({ to: '/app/content', search: next === 'content' ? {} : { tab: next } });
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="h2-eyebrow">Áttekintés</div>
        <h1 className="mt-1.5 text-h1 font-sans font-medium leading-none tracking-tight text-[var(--ink-700)]">
          Tartalmak{' '}
          {tab === 'content' && (
            <span className="font-mono text-h2 font-normal text-[var(--fg-faint)]">
              {contentCount}
            </span>
          )}
        </h1>
      </div>

      {/* Tab switcher — reuses the pill toggle style from the approvals page. */}
      <div className="inline-flex rounded-pill border border-[var(--app-divider-2)] bg-[var(--app-surface)] p-0.5">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => selectTab(t.value)}
            aria-pressed={tab === t.value}
            className={cn(
              'inline-flex h-8 items-center rounded-pill px-3 text-meta font-medium transition-colors',
              tab === t.value
                ? 'bg-[var(--brand-500)] text-white'
                : 'text-[var(--fg-2)] hover:text-[var(--ink-700)]',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'content' ? <ContentPanel onRowCount={setContentCount} /> : <ReservePanel />}
    </div>
  );
}

function ContentPage() {
  return (
    <AuthGate roles={['admin', 'editor', 'viewer']}>
      <ContentInner />
    </AuthGate>
  );
}

export const Route = createFileRoute('/app/content')({
  validateSearch: (s: Record<string, unknown>): { tab?: ContentTab } => {
    return s.tab === 'reserve' ? { tab: 'reserve' } : { tab: 'content' };
  },
  component: ContentPage,
});
